import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Percent, Loader2, Plus, Trash2, Save } from "lucide-react";

export default function BonusFiscali() {
  const [base, setBase] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get("/settings/condizioni")
      .then((r) => { setBase(r.data); setItems(r.data.bonus || []); })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, []);

  const setRow = (i, k, v) => setItems((arr) => arr.map((row, idx) => (idx === i ? { ...row, [k]: v } : row)));
  const remove = (i) => setItems((arr) => arr.filter((_, idx) => idx !== i));

  const save = async () => {
    const b = items.map((c) => ({ nome: (c.nome || "").trim(), percentuale: Number(c.percentuale) || 0, anni: Number(c.anni) || 0 }));
    if (b.some((c) => !c.nome)) { toast.error("Ogni bonus deve avere un nome"); return; }
    setSaving(true);
    try {
      const { data } = await api.put("/settings/condizioni", { ...base, bonus: b });
      setBase(data); setItems(data.bonus || []);
      toast.success("Bonus fiscali salvati");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="bonus-page">
      <header className="sticky top-14 lg:top-0 z-10 h-16 border-b border-border bg-background flex items-center justify-between px-4 lg:px-8">
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-primary/10 p-2"><Percent className="h-5 w-5 text-primary" strokeWidth={1.5} /></div>
          <div>
            <h1 className="text-xl font-heading font-bold">Bonus fiscali</h1>
            <p className="text-xs text-muted-foreground">Detrazioni selezionabili nel Preventivo</p>
          </div>
        </div>
        <Button onClick={save} disabled={saving || loading} data-testid="save-bonus-btn" className="font-semibold">
          {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} Salva
        </Button>
      </header>

      <div className="p-4 lg:p-8 max-w-4xl">
        {loading ? (
          <div className="flex h-40 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
        ) : (
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground mb-1">Ogni bonus è una <b>percentuale</b> applicata sul prezzo (IVA incl.) ripartita negli <b>anni</b> indicati. Impatta rientro economico, risparmi e grafici. Default residenziale: <b>50% in 10 anni</b>.</p>
            {items.map((c, i) => (
              <Card key={i} className="rounded-lg border-border p-4" data-testid={`bonus-row-${i}`}>
                <div className="flex items-end gap-3">
                  <div className="flex-1">
                    <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Nome bonus</Label>
                    <Input value={c.nome} onChange={(e) => setRow(i, "nome", e.target.value)} placeholder="Detrazione ristrutturazione 50% (residenziale)" data-testid={`bonus-nome-${i}`} />
                  </div>
                  <div className="w-28">
                    <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Beneficio %</Label>
                    <Input type="number" value={c.percentuale} onChange={(e) => setRow(i, "percentuale", e.target.value === "" ? "" : Number(e.target.value))} placeholder="50" data-testid={`bonus-pct-${i}`} />
                  </div>
                  <div className="w-28">
                    <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Anni</Label>
                    <Input type="number" value={c.anni} onChange={(e) => setRow(i, "anni", e.target.value === "" ? "" : Number(e.target.value))} placeholder="10" data-testid={`bonus-anni-${i}`} />
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => remove(i)} className="text-destructive hover:text-destructive shrink-0" data-testid={`bonus-remove-${i}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </Card>
            ))}
            <Button variant="outline" size="sm" onClick={() => setItems((arr) => [...arr, { nome: "", percentuale: 0, anni: 0 }])} data-testid="bonus-add">
              <Plus className="h-4 w-4 mr-1.5" /> Aggiungi bonus fiscale
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
